import React, { useState, useEffect, useRef } from "react";

const CompactMncPortal = () => {
  const [isMuted, setIsMuted] = useState(true);
  const [playingId, setPlayingId] = useState(null);
  const [courses, setCourses] = useState([]);
  const videoRefs = useRef({});

  // Fetch API
  useEffect(() => {
    fetch("https://aadhavacademy.com/video/data.json")
      .then((res) => res.json())
      .then((data) => setCourses(data))
      .catch((err) => console.error("API Error:", err));
  }, []);

  // Play / Pause Logic (Single Video Focus)
  const togglePlay = (id) => {
    // 1. Pause all other videos
    Object.entries(videoRefs.current).forEach(([key, video]) => {
      if (video && Number(key) !== id) {
        video.pause();
        // Resetting currentTime is optional, but helps with the 'focus' feel
        video.currentTime = 0; 
      }
    });

    const video = videoRefs.current[id];
    if (!video) return;

    if (video.paused) {
      // YouTube behavior: Clicking to play usually implies wanting sound
      setIsMuted(false); 
      video.play().catch(() => {}); 
      setPlayingId(id);
    } else {
      video.pause();
      setPlayingId(null);
    }
  };

  return (
    <div style={styles.appContainer}>
      {/* COMPACT NAV BAR */}
      <nav style={styles.nav}>
        <div style={styles.navInner}>
          <div style={styles.navLeft}>
            <img
              src="https://aadhavacademy.com/video/nlh.jpeg"
              alt="Logo"
              style={styles.logoImg}
            />
            <div style={styles.verticalDivider}></div>
            <h1 style={styles.welcomeText}>
              Welcome to New Learning Horizons Centralized
            </h1>
          </div>

          <div style={styles.navRight}>
            <button
              onClick={(e) => {
                e.stopPropagation(); // Prevent triggering any card clicks
                setIsMuted(!isMuted);
              }}
              style={styles.volumeBtn}
            >
              {isMuted ? "🔇 Muted" : "🔊 Audio On"}
            </button>
            <div style={styles.profileCircle}>AA</div>
          </div>
        </div>
      </nav>

      {/* CONTENT STARTING AT THE TOP */}
      <main style={styles.content}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.rowTitle}>Core Competency Library</h2>
        </div>

        <div style={styles.cardGrid}>
          {courses.map((course) => (
            <div key={course.id} style={styles.card}>
              {/* Clickable Video Container like YouTube */}
              <div 
                style={styles.mediaWrapper} 
                onClick={() => togglePlay(course.id)}
              >
                <video
                  ref={(el) => (videoRefs.current[course.id] = el)}
                  src={course.video}
                  muted={isMuted}
                  playsInline
                  onEnded={() => setPlayingId(null)}
                  style={styles.videoPreview}
                />

                {/* Overlay Play Indicator */}
                <div style={{
                  ...styles.playOverlay,
                  opacity: playingId === course.id ? 0 : 1
                }}>
                  <div style={styles.playIconBox}>▶</div>
                </div>

                {/* Small Play/Pause Button Bottom Left */}
                <button
                  style={styles.playPauseBtn}
                  onClick={(e) => {
                    e.stopPropagation(); // Stop click from bubbling to mediaWrapper
                    togglePlay(course.id);
                  }}
                >
                  {playingId === course.id ? "⏸" : "▶"}
                </button>
              </div>

              <div style={styles.details}>
                <h3 style={styles.courseTitle}>{course.title}</h3>
                <ul style={styles.featureList}>
                  {course.features.map((feature, index) => (
                    <li key={index} style={styles.featureItem}>
                      <span style={styles.checkIcon}>✓</span> {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
};

/* ===================== UPDATED STYLES ===================== */

const styles = {
  appContainer: { backgroundColor: "#fff", minHeight: "100vh", fontFamily: "Inter, sans-serif" },

  nav: {
    position: "fixed", top: 0, left: 0, right: 0, height: "50px",
    backgroundColor: "#ffffff", borderBottom: "1px solid #eee",
    zIndex: 1000, display: "flex", justifyContent: "center"
  },

  navInner: {
    width: "100%", maxWidth: "1440px", padding: "0 20px",
    display: "flex", justifyContent: "space-between", alignItems: "center"
  },

  navLeft: { display: "flex", alignItems: "center", gap: "15px" },
  logoImg: { height: "30px" },
  verticalDivider: { width: "1px", height: "20px", backgroundColor: "#ddd" },
  welcomeText: { fontSize: "13px", fontWeight: "600", color: "#444" },

  navRight: { display: "flex", alignItems: "center", gap: "12px" },
  volumeBtn: {
    background: "#f5f5f5", border: "1px solid #ddd", padding: "4px 10px",
    borderRadius: "4px", fontSize: "11px", fontWeight: "600", cursor: "pointer"
  },
  profileCircle: {
    width: "28px", height: "28px", borderRadius: "50%",
    backgroundColor: "#111", color: "#fff", display: "flex",
    alignItems: "center", justifyContent: "center", fontSize: "10px", fontWeight: "bold"
  },

  content: {
    padding: "30px 20px 30px", // Adjusted to bring Library Title to top
    maxWidth: "1440px", margin: "0 auto"
  },

  sectionHeader: { marginBottom: "15px" },
  rowTitle: { fontSize: "18px", fontWeight: "800", color: "#111" },

  cardGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: "20px"
  },

  card: { backgroundColor: "#fff" },

  mediaWrapper: {
    aspectRatio: "16/9",
    position: "relative",
    backgroundColor: "#000",
    borderRadius: "8px",
    overflow: "hidden",
    cursor: "pointer" // Shows user it is clickable
  },

  videoPreview: { width: "100%", height: "100%", objectFit: "cover" },

  // Large center play icon that disappears on play
  playOverlay: {
    position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
    display: "flex", justifyContent: "center", alignItems: "center",
    backgroundColor: "rgba(0,0,0,0.2)", transition: "opacity 0.3s"
  },
  playIconBox: {
    width: "50px", height: "50px", backgroundColor: "rgba(255,255,255,0.9)",
    borderRadius: "50%", display: "flex", justifyContent: "center", 
    alignItems: "center", fontSize: "20px", color: "#000"
  },

  playPauseBtn: {
    position: "absolute", bottom: "8px", left: "8px",
    background: "rgba(255,255,255,0.9)", border: "none",
    width: "26px", height: "26px", borderRadius: "4px",
    fontSize: "12px", cursor: "pointer", zIndex: 5
  },

  details: { padding: "10px 0" },
  courseTitle: { fontSize: "15px", fontWeight: "700", marginBottom: "4px", color: "#111" },
  featureList: { listStyle: "none", padding: 0, margin: 0 },
  featureItem: {
    fontSize: "13px", color: "#666", display: "flex",
    alignItems: "center", gap: "6px", marginBottom: "2px"
  },
  checkIcon: { color: "#10B981", fontWeight: "bold" }
};

export default CompactMncPortal;